import { csvHeader, csvText, errorReport, MAX_IMPORT_ROWS, type ImportPlan } from '@cyberscore/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Download, FileUp, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { save } from '../evidence/api';
import { useClientFields } from './api';
import { refreshLists } from '../../lib/list';

const ACTION: Record<ImportPlan['rows'][number]['action'], [string, string]> = {
  create: ['New', 'ok'],
  update: ['Update', 'warn'],
  unchanged: ['No change', 'neutral'],
  error: ['Error', 'bad'],
};

const form = (file: File, digest?: string) => {
  const f = new FormData();
  f.append('file', file);
  if (digest) f.append('digest', digest);
  return f;
};

/**
 * Import clients from a CSV (task 67): the file is checked first and every row shown as new, an update
 * with what changes, unchanged or an error; nothing is saved until the person imports what they saw.
 */
export function ImportClients({ onClose }: { onClose: () => void }) {
  const fields = useClientFields();
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const preview = async (f: File) => {
    setBusy(true);
    setBanner(null);
    setPlan(null);
    try {
      setText(await f.text());
      setPlan(await api<ImportPlan>('POST', '/clients/import/preview', form(f)));
    } catch (e) {
      setBanner(e instanceof ApiError ? e.message : 'Could not read the file');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!file || !plan) return;
    setBusy(true);
    setBanner(null);
    try {
      const done = await api<ImportPlan>('POST', '/clients/import', form(file, plan.digest));
      await refreshLists(qc);
      toast(`Imported: ${done.counts.create} new, ${done.counts.update} updated.`);
      onClose();
    } catch (e) {
      setBanner(e instanceof ApiError ? e.message : 'Could not import. Try again.');
      if (e instanceof ApiError && e.status === 409) void preview(file);
    } finally {
      setBusy(false);
    }
  };

  const saving = plan ? plan.counts.create + plan.counts.update : 0;
  const shown = plan ? plan.rows.filter((r) => showAll || r.action !== 'unchanged') : [];
  const template = () =>
    save(new Blob([csvText([csvHeader(fields.data ?? [])])], { type: 'text/csv;charset=utf-8' }), 'clients-template.csv'); // prettier-ignore

  return (
    <Drawer
      title="Import clients"
      subtitle={`From a CSV in the export’s format, up to ${MAX_IMPORT_ROWS} clients. You see every change before anything is saved.`}
      onClose={onClose}
      footer={
        <>
          {plan && plan.counts.error > 0 && (
            <button type="button" className="btn" onClick={() => save(new Blob([errorReport(text, plan)], { type: 'text/csv;charset=utf-8' }), 'clients-import-errors.csv')}>
              <Download size={15} /> Error report
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!plan || !saving || busy} onClick={commit}>
            {busy && plan && <Loader2 size={15} className="spin" />}
            {saving ? `Import ${saving} client${saving === 1 ? '' : 's'}` : 'Import'}
          </button>
        </>
      }
    >
      {banner && <div className="result bad" role="alert"><CircleAlert size={16} /><span>{banner}</span></div>}
      <div className="field">
        <label htmlFor="import-file">CSV file</label>
        <input id="import-file" type="file" accept=".csv,text/csv" className="input" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f) void preview(f); }} />
        <span className="hint">
          Export your clients for a file to start from, or{' '}
          <button type="button" className="linklike" onClick={template}>download an empty template</button>. A blank cell leaves a value as it is; a single - clears it.
        </span>
      </div>
      {busy && !plan && <div className="empty"><Loader2 size={20} className="spin" />Checking the file…</div>}
      {plan && plan.file_issues.length > 0 && (
        <div className="result bad" role="alert"><CircleAlert size={16} /><span>{plan.file_issues.join('. ')}</span></div>
      )}
      {plan && !plan.file_issues.length && (
        <>
          <div className="import-counts" aria-label="What the import would do">
            <span className="pill ok">{plan.counts.create} new</span>
            <span className="pill warn">{plan.counts.update} to update</span>
            <span className="pill neutral">{plan.counts.unchanged} unchanged</span>
            <span className={`pill ${plan.counts.error ? 'bad' : 'neutral'}`}>{plan.counts.error} with errors</span>
          </div>
          {plan.counts.error > 0 && <p className="muted small">Rows with errors are skipped. Download the error report, fix those rows and import it again.</p>}
          <div className="table-wrap">
            <table className="grid import-rows">
              <thead><tr><th>Line</th><th>Client</th><th>Result</th><th>Details</th></tr></thead>
              <tbody>
                {shown.slice(0, 200).map((r) => (
                  <tr key={r.line}>
                    <td className="num">{r.line}</td>
                    <td className="mono small">{r.customer_key || '–'}</td>
                    <td><span className={`pill ${ACTION[r.action][1]}`}>{ACTION[r.action][0]}</span></td>
                    <td className="small">
                      {r.action === 'error'
                        ? r.issues.map((i) => (i.column ? `${i.column}: ${i.message}` : i.message)).join('; ')
                        : r.changes.join(', ') || '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown.length > 200 && <p className="muted small">Showing the first 200 of {shown.length}.</p>}
          {plan.counts.unchanged > 0 && (
            <button type="button" className="linklike left" onClick={() => setShowAll((x) => !x)}>
              {showAll ? 'Hide unchanged rows' : `Show the ${plan.counts.unchanged} unchanged rows too`}
            </button>
          )}
        </>
      )}
      {!plan && !busy && (
        <div className="empty"><FileUp size={26} /><strong>Choose a CSV</strong>Columns: customer_key, customer_name, stage, policy_number, line_of_business, premium, sum_insured, renewal_date, broker, industry, country, then your client fields by key.</div>
      )}
    </Drawer>
  ); // prettier-ignore
}
